import { useRef, useState, type SyntheticEvent } from "react";
import {
  sendSystemAccountBatchMessage,
  sendSystemAccountGlobalBroadcast,
  sendSystemAccountMessage,
  type Application,
  type SystemAccount,
  type SystemAccountMessageDraft,
  type SystemAccountMessageType,
} from "../api";
import { Button } from "./ui/Button";
import { ConfirmDialog } from "./ui/ConfirmDialog";

interface PreparedMessage {
  readonly targetType: "USER" | "GROUP" | "GROUP_USERS" | "GLOBAL_ONLINE";
  readonly targetId: string;
  readonly recipientUserIds: readonly string[];
  readonly message: SystemAccountMessageDraft["message"];
}

/**
 * 系统账号消息编辑器，同时服务独立发信工作台和账号列表快捷入口。
 * 同一次用户操作会复用 Idempotency-Key；只有明确成功后才生成新 Key。
 */
export function SystemMessageComposer({ application, account, onCancel, onError, onSent }: {
  readonly application: Application;
  readonly account: SystemAccount;
  readonly onCancel?: () => void;
  readonly onError: (message: string) => void;
  readonly onSent: (message: string) => void;
}) {
  const [targetType, setTargetType] = useState<"USER" | "GROUP" | "GROUP_USERS" | "GLOBAL_ONLINE">("USER");
  const [targetId, setTargetId] = useState("");
  const [batchUserIds, setBatchUserIds] = useState("");
  const [messageType, setMessageType] = useState<SystemAccountMessageType>(account.allowedMessageTypes[0] ?? "text");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<PreparedMessage | null>(null);
  const idempotencyKey = useRef(crypto.randomUUID());

  function prepare(): PreparedMessage | null {
    const normalizedTargetId = targetId.trim();
    const normalizedContent = content.trim();
    if (targetType !== "GLOBAL_ONLINE" && normalizedTargetId.length === 0) {
      onError(targetType === "USER" ? "IM 用户 ID 不能为空" : "群组 ID 不能为空");
      return null;
    }
    if (normalizedContent.length === 0) {
      onError(messageType === "text" ? "消息内容不能为空" : "Payload 不能为空");
      return null;
    }
    const recipientUserIds = [...new Set(batchUserIds.split(/[,，;；\s]+/u).map((value) => value.trim()).filter(Boolean))];
    if (targetType === "GROUP_USERS" && recipientUserIds.length === 0) {
      onError("请至少填写一个群成员用户 ID");
      return null;
    }
    if (recipientUserIds.length > 100) {
      onError("单次最多向 100 个群成员批量推送");
      return null;
    }
    if (recipientUserIds.some((userId) => userId.length > 128)) {
      onError("群成员用户 ID 不能超过 128 个字符");
      return null;
    }

    let message: SystemAccountMessageDraft["message"];
    if (messageType === "text") {
      message = { type: "TEXT", text: content };
    } else {
      try {
        message = { type: serverMessageType(messageType), payload: JSON.parse(normalizedContent) as unknown };
      } catch {
        onError("非文本消息的 Payload 必须是有效 JSON");
        return null;
      }
    }
    return { targetType, targetId: normalizedTargetId, recipientUserIds, message };
  }

  async function execute(prepared: PreparedMessage): Promise<void> {
    setBusy(true);
    try {
      if (prepared.targetType === "GLOBAL_ONLINE") {
        const ack = await sendSystemAccountGlobalBroadcast(application.appId, account.systemUserId, {
          message: prepared.message,
        }, idempotencyKey.current);
        onSent(`全局在线广播已完成一次实时投递尝试；目标为 ${ack.activeShardCount} 个活跃连接分片`);
      } else if (prepared.targetType === "GROUP_USERS") {
        const ack = await sendSystemAccountBatchMessage(application.appId, account.systemUserId, {
          to: { type: "GROUP_USERS", groupId: prepared.targetId, userIds: prepared.recipientUserIds },
          message: prepared.message,
        }, idempotencyKey.current);
        onSent(`批量消息已发送给 ${ack.recipientCount} 位群成员，批次 ID ${ack.batchId}`);
      } else {
        const ack = await sendSystemAccountMessage(application.appId, account.systemUserId, {
          to: prepared.targetType === "USER"
            ? { type: "USER", userId: prepared.targetId }
            : { type: "GROUP", groupId: prepared.targetId },
          message: prepared.message,
        }, idempotencyKey.current);
        onSent(`消息已可靠接受，序号 ${ack.seq}，消息 ID ${ack.messageId}`);
      }
      idempotencyKey.current = crypto.randomUUID();
      setContent("");
      setBatchUserIds("");
      setPending(null);
    } catch (cause) {
      onError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  function submit(event: SyntheticEvent<HTMLFormElement>): void {
    event.preventDefault();
    const prepared = prepare();
    if (prepared === null) return;
    if (prepared.targetType === "USER") void execute(prepared);
    else setPending(prepared);
  }

  return <>
    <form className="dialog-form system-message-composer" onSubmit={submit}>
      <div className="form-grid">
        <label className="field"><span>发送目标 <em>*</em></span><select value={targetType} onChange={(event) => { setTargetType(event.currentTarget.value as typeof targetType); }}><option value="USER">指定 IM 用户</option><option value="GROUP">指定群组 / 广播</option><option value="GROUP_USERS">群内指定用户（批量单聊）</option><option value="GLOBAL_ONLINE">全局在线用户（临时广播）</option></select></label>
        {targetType === "GLOBAL_ONLINE" ? <div className="field"><span>投递范围</span><div className="readonly-field">当前应用的在线用户</div><small>不创建会话，不持久化消息、离线记录或未读数；断线或之后上线的用户不会收到。</small></div> : <label className="field"><span>{targetType === "USER" ? "IM 用户 ID" : "群组 ID"} <em>*</em></span><input required maxLength={128} placeholder={targetType === "USER" ? "例如 u123456" : "例如 group_xxx 或 broadcast_xxx"} value={targetId} onChange={(event) => { setTargetId(event.currentTarget.value); }} /></label>}
        {targetType === "GROUP_USERS" ? <label className="field field-full"><span>群成员用户 ID <em>*</em></span><textarea required rows={4} placeholder="每行一个用户 ID，也支持逗号或空格分隔；单次最多 100 人" value={batchUserIds} onChange={(event) => { setBatchUserIds(event.currentTarget.value); }} /><small>所有用户必须是目标群当前的活跃成员；每人会收到一条独立、可安全重试的系统账号单聊。</small></label> : null}
        <label className="field field-full"><span>消息类型 <em>*</em></span><select value={messageType} onChange={(event) => { setMessageType(event.currentTarget.value as SystemAccountMessageType); setContent(""); }}>{account.allowedMessageTypes.map((type) => <option key={type} value={type}>{messageTypeLabel(type)}</option>)}</select><small>仅展示当前系统账号允许发送的消息类型。</small></label>
        <label className="field field-full"><span>{messageType === "text" ? "消息内容" : "Payload（JSON）"} <em>*</em></span><textarea required rows={8} maxLength={32 * 1024} placeholder={messageType === "text" ? "请输入要发送的消息" : payloadPlaceholder(messageType)} value={content} onChange={(event) => { setContent(event.currentTarget.value); }} /><small>{targetType === "GROUP" ? "群组与广播消息会写入对应会话历史并分配 seq。" : targetType === "GROUP_USERS" ? "批量消息不会写入群历史，也不会改变群会话 seq。" : targetType === "GLOBAL_ONLINE" ? "消息正文与投递任务均不持久化，只进行一次当前在线连接的实时投递；失败不会离线补偿。" : "系统会自动建立或复用与该用户的稳定 P2P 会话。"}</small></label>
      </div>
      <div className="composer-safety-note"><strong>{targetType === "GLOBAL_ONLINE" ? "请求标识" : "安全重试标识"}</strong><code>{idempotencyKey.current}</code><span>{targetType === "GLOBAL_ONLINE" ? "仅在同一运行实例内短暂去重；响应未知时重试可能造成重复实时投递。" : "网络失败后再次提交会继续使用该标识，明确成功后自动更新。"}</span></div>
      <footer className="dialog-footer">{onCancel === undefined ? null : <Button disabled={busy} type="button" onClick={onCancel}>取消</Button>}<Button loading={busy} type="submit" variant="primary">{targetType === "USER" ? "发送消息" : targetType === "GLOBAL_ONLINE" ? "发送全局广播" : "预览并发送"}</Button></footer>
    </form>
    {pending === null ? null : <ConfirmDialog busy={busy} confirmLabel="确认发送" danger={pending.targetType !== "USER"} title={pending.targetType === "GROUP_USERS" ? "确认批量发送" : pending.targetType === "GLOBAL_ONLINE" ? "确认发送全局在线广播" : "确认发送群组消息"} description={pending.targetType === "GLOBAL_ONLINE" ? `系统账号 ${account.displayName} 将向当前应用的全部在线用户发送临时广播。` : `系统账号 ${account.displayName} 将向目标 ${pending.targetId} 发送消息。`} consequences={pending.targetType === "GROUP_USERS" ? [`接收用户数：${pending.recipientUserIds.length}`, "每位用户将收到一条独立单聊消息", "发送成功后无法从接收端撤销"] : pending.targetType === "GLOBAL_ONLINE" ? ["消息正文和投递任务均不持久化", "只尝试投递到当前在线连接；失败、断线和后续上线均不会补发", "响应未知时重试可能造成重复实时投递"] : ["消息会写入目标会话历史", "消息将占用会话 seq 并触发在线投递", "广播可能同时触达大量在线用户"]} onClose={() => { if (!busy) setPending(null); }} onConfirm={() => { void execute(pending); }} />}
  </>;
}

function messageTypeLabel(type: SystemAccountMessageType): string {
  return ({ text: "文本", image: "图片", audio: "语音", video: "视频", file: "文件", custom: "自定义", command: "CMD 指令" })[type];
}

function serverMessageType(type: SystemAccountMessageType): "IMAGE" | "AUDIO" | "VIDEO" | "FILE" | "CUSTOM" | "COMMAND" {
  switch (type) {
    case "image": return "IMAGE";
    case "audio": return "AUDIO";
    case "video": return "VIDEO";
    case "file": return "FILE";
    case "custom": return "CUSTOM";
    case "command": return "COMMAND";
    case "text": throw new Error("文本消息不使用结构化 Payload");
  }
}

function payloadPlaceholder(type: SystemAccountMessageType): string {
  switch (type) {
    case "image": return '{"uuid":"image-001","format":"jpeg","original":{"objectKey":"media/v2/...","sizeBytes":1024}}';
    case "audio": return '{"uuid":"audio-001","source":{"objectKey":"media/v2/...","sizeBytes":1024},"durationMs":1000,"codec":"opus"}';
    case "video": return '{"version":1,"kind":"video","uuid":"video-001","source":{"objectKey":"media/v2/...","sizeBytes":1024},"durationMs":1000}';
    case "file": return '{"version":1,"kind":"file","uuid":"file-001","source":{"objectKey":"media/v2/...","sizeBytes":1024},"fileName":"document.pdf"}';
    case "custom": return '{"data":{"key":"value"},"description":"业务通知","extension":"notice-v1"}';
    case "command": return '{"name":"refresh","parameters":{},"requireAck":false,"priority":"low"}';
    case "text": return "请输入要发送的消息";
  }
}

function errorMessage(cause: unknown): string { return cause instanceof Error ? cause.message : String(cause); }
